import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  message,
  Popconfirm,
  Typography,
  Divider,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MailOutlined,
  PlayCircleOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { adminApi } from '@/services/api';
import { ConfigVersionSelect } from '@/components/ConfigVersionSelect';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

// Enums matching backend
const InboxMessageType = {
  ANNOUNCEMENT: 'announcement',
  REWARD: 'reward',
  PROMOTIONAL: 'promotional',
  MAINTENANCE: 'maintenance',
  UPDATE: 'update',
  TOURNAMENT: 'tournament',
  EVENT: 'event',
  PERSONAL: 'personal',
  ACHIEVEMENT: 'achievement',
  WELCOME: 'welcome',
} as const;

const InboxMessageStatus = {
  DRAFT: 'draft',
  SCHEDULED: 'scheduled',
  ACTIVE: 'active',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
} as const;

const InboxMessagePriority = {
  LOW: 'low',
  NORMAL: 'normal',
  HIGH: 'high',
  URGENT: 'urgent',
} as const;

const TargetAudienceType = {
  ALL: 'all',
  SEGMENT: 'segment',
  USER_LIST: 'user_list',
} as const;

interface InboxMessage {
  id: string;
  title: string;
  body: string;
  shortDescription?: string;
  type: (typeof InboxMessageType)[keyof typeof InboxMessageType];
  status: (typeof InboxMessageStatus)[keyof typeof InboxMessageStatus];
  priority: (typeof InboxMessagePriority)[keyof typeof InboxMessagePriority];
  imageUrl?: string;
  iconUrl?: string;
  buttonText?: string;
  buttonAction?: string;
  targeting: {
    audienceType: string;
    userIds?: string[];
    segment?: Record<string, unknown>;
  };
  startsAt: string;
  expiresAt?: string;
  displayDurationDays?: number;
  platform?: string;
  minBuildVersion?: number;
  maxBuildVersion?: number;
  deliveredCount: number;
  readCount: number;
  claimedCount: number;
  clickCount: number;
  createdAt: string;
  updatedAt: string;
}

const statusColors: Record<string, string> = {
  draft: 'default',
  scheduled: 'blue',
  active: 'green',
  expired: 'gray',
  cancelled: 'red',
};

const priorityColors: Record<string, string> = {
  low: 'default',
  normal: 'blue',
  high: 'orange',
  urgent: 'red',
};

const typeLabels: Record<string, string> = {
  announcement: 'Announcement',
  reward: 'Reward',
  promotional: 'Promotional',
  maintenance: 'Maintenance',
  update: 'Update',
  tournament: 'Tournament',
  event: 'Event',
  personal: 'Personal',
  achievement: 'Achievement',
  welcome: 'Welcome',
};

const InboxPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getInboxMessages();
      // Backend returns { data: [...], meta: {...} }
      const data = res.data?.data || res.data?.items || res.data;
      setMessages(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to fetch inbox messages', error);
      message.error('Failed to load inbox messages');
      setMessages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (values: Record<string, unknown>) => {
    setSubmitting(true);
    try {
      const formValues = values as {
        startsAt?: dayjs.Dayjs;
        expiresAt?: dayjs.Dayjs;
        audienceType?: string;
      } & Record<string, unknown>;

      const { startsAt, expiresAt, audienceType, ...otherValues } = formValues;

      const payload = {
        ...otherValues,
        startsAt: startsAt?.toISOString(),
        expiresAt: expiresAt?.toISOString(),
        targeting: {
          audienceType: audienceType || 'all',
        },
      };

      if (editingId) {
        await adminApi.updateInboxMessage(editingId, payload);
        message.success('Message updated');
      } else {
        await adminApi.createInboxMessage(payload);
        message.success('Message created');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingId(null);
      fetchData();
    } catch (error) {
      console.error('Failed to save message', error);
      message.error('Failed to save message');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (record: InboxMessage) => {
    setEditingId(record.id);
    form.setFieldsValue({
      ...record,
      startsAt: record.startsAt ? dayjs(record.startsAt) : undefined,
      expiresAt: record.expiresAt ? dayjs(record.expiresAt) : undefined,
      audienceType: record.targeting?.audienceType || 'all',
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await adminApi.deleteInboxMessage(id);
      message.success('Message deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete message', error);
      message.error('Failed to delete message');
    }
  };

  const handleActivate = async (id: string) => {
    try {
      await adminApi.updateInboxMessage(id, { status: InboxMessageStatus.ACTIVE });
      message.success('Message activated');
      fetchData();
    } catch (error) {
      console.error('Failed to activate', error);
      message.error('Failed to activate message');
    }
  };

  const handleCancel = async (id: string) => {
    try {
      await adminApi.updateInboxMessage(id, { status: InboxMessageStatus.CANCELLED });
      message.success('Message cancelled');
      fetchData();
    } catch (error) {
      console.error('Failed to cancel', error);
      message.error('Failed to cancel message');
    }
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (title: string, record: InboxMessage) => (
        <div>
          <Text strong>{title}</Text>
          {record.shortDescription && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {record.shortDescription}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => <Tag color="blue">{typeLabels[type] || type}</Tag>,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: string) => (
        <Tag color={priorityColors[priority]}>
          {priority.charAt(0).toUpperCase() + priority.slice(1)}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status] || 'default'}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </Tag>
      ),
    },
    {
      title: 'Stats',
      key: 'stats',
      render: (_: unknown, record: InboxMessage) => (
        <Space direction="vertical" size={0}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            📬 {record.deliveredCount || 0} | 👁 {record.readCount || 0}
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            🎁 {record.claimedCount || 0} | 👆 {record.clickCount || 0}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Starts At',
      dataIndex: 'startsAt',
      key: 'startsAt',
      render: (date: string) => (date ? dayjs(date).format('MMM D, HH:mm') : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: InboxMessage) => (
        <Space>
          {record.status === 'draft' || record.status === 'scheduled' ? (
            <Button
              icon={<PlayCircleOutlined />}
              size="small"
              type="primary"
              onClick={() => handleActivate(record.id)}
            >
              Activate
            </Button>
          ) : record.status === 'active' ? (
            <Button icon={<StopOutlined />} size="small" onClick={() => handleCancel(record.id)}>
              Cancel
            </Button>
          ) : null}
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Popconfirm title="Delete this message?" onConfirm={() => handleDelete(record.id)}>
            <Button icon={<DeleteOutlined />} size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <Space>
            <MailOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Inbox Messages
            </Title>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingId(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            New Message
          </Button>
        }
      >
        <Table
          dataSource={messages}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingId ? 'Edit Message' : 'Create Message'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingId(null);
        }}
        onOk={() => form.submit()}
        width={800}
        confirmLoading={submitting}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input placeholder="Message title" />
          </Form.Item>

          <Form.Item name="body" label="Message Body" rules={[{ required: true }]}>
            <TextArea rows={4} placeholder="Full message content" />
          </Form.Item>

          <Form.Item name="shortDescription" label="Short Description">
            <Input placeholder="Preview text for inbox list" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="type" label="Message Type" rules={[{ required: true }]}>
                <Select placeholder="Select type">
                  {Object.entries(InboxMessageType).map(([key, value]) => (
                    <Select.Option key={value} value={value}>
                      {typeLabels[value] || key}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="status" label="Status" initialValue={InboxMessageStatus.DRAFT}>
                <Select>
                  {Object.entries(InboxMessageStatus).map(([key, value]) => (
                    <Select.Option key={value} value={value}>
                      {key.charAt(0) + key.slice(1).toLowerCase()}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="priority"
                label="Priority"
                initialValue={InboxMessagePriority.NORMAL}
              >
                <Select>
                  {Object.entries(InboxMessagePriority).map(([key, value]) => (
                    <Select.Option key={value} value={value}>
                      {key.charAt(0) + key.slice(1).toLowerCase()}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Targeting</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="audienceType"
                label="Target Audience"
                initialValue={TargetAudienceType.ALL}
              >
                <Select>
                  <Select.Option value="all">All Players</Select.Option>
                  <Select.Option value="segment">Segment</Select.Option>
                  <Select.Option value="user_list">Specific Users</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="platform" label="Platform">
                <Select placeholder="All" allowClear>
                  <Select.Option value="ios">iOS</Select.Option>
                  <Select.Option value="android">Android</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="configVersionId"
                label="Config Version"
                extra="Leave empty to apply to every build. Pick a config version to limit this to the app versions mapped to it."
              >
                <ConfigVersionSelect style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item name="minBuildVersion" label="Min Build Version">
                <InputNumber style={{ width: '100%' }} placeholder="e.g., 100" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Scheduling</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="startsAt" label="Starts At" rules={[{ required: true }]}>
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="expiresAt" label="Expires At">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="displayDurationDays" label="Display Duration (Days)">
                <InputNumber style={{ width: '100%' }} placeholder="Days to show after read" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Visual & CTA</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="imageUrl" label="Banner Image URL">
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="iconUrl" label="Icon URL">
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="buttonText" label="Button Text">
                <Input placeholder="e.g., Claim Now" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="buttonAction" label="Button Action (Deeplink)">
                <Input placeholder="wcc4://..." />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default InboxPage;
